// Enviar SMS para um contato no padrão Aeros Dark (mesmo esqueleto dos
// modais de Contatos: cabeçalho com subtítulo, card do contato, campo com
// label acima, rodapé com divisor). A lógica é a de sempre: GET /sms/saldo
// ao abrir e POST /sms/send { contactId, corpo }.
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import SmsIcon from "@material-ui/icons/Sms";
import SendIcon from "@material-ui/icons/Send";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { avatarColorFor, initialsOf, formatPhone } from "../../pages/Contacts/contactRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    paper: { width: 480, maxWidth: "calc(100vw - 32px)", margin: 16 },
    body: { gap: 14 },
    contactCard: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 12,
      padding: "10px 14px",
    },
    contactName: { fontSize: 13.5, fontWeight: 600, color: theme.palette.text.primary },
    contactNumber: { fontSize: 11.5, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
    labelRow: { display: "flex", alignItems: "center", marginBottom: 5 },
    counter: { marginLeft: "auto", fontSize: 11, color: theme.palette.aeros.textTertiary, fontVariantNumeric: "tabular-nums" },
    saldoDepois: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    counterWarn: { color: stateText(theme, AEROS.warning, "warning") },
    saldoPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      color: stateText(theme, AEROS.cyan, "info"),
      backgroundColor: alpha(AEROS.cyan, 0.1),
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    saldoZero: { color: stateText(theme, AEROS.error, "error"), backgroundColor: alpha(AEROS.error, 0.1) },
  };
});

// Duplicata em JS da regra de fracionamento do backend (é exibição, não
// cobrança — a contagem oficial é sempre feita no servidor no envio).
export const calcularFracoesSms = (texto) => {
  const len = (texto || "").length;
  if (len === 0) return 0;
  return len <= 160 ? 1 : Math.ceil(len / 153);
};

const EnviarSmsModal = ({ open, onClose, contact }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [corpo, setCorpo] = useState("");
  const [saldo, setSaldo] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCorpo("");
    api
      .get("/sms/saldo")
      .then(({ data }) => setSaldo(data?.saldo ?? null))
      .catch(() => setSaldo(null));
  }, [open]);

  const handleClose = () => {
    if (sending) return;
    setCorpo("");
    onClose();
  };

  const handleEnviar = async () => {
    if (!corpo.trim()) {
      toast.error(i18n.t("smsModal.emptyBody"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/sms/send", { contactId: contact.id, corpo: corpo.trim() });
      toast.success(i18n.t("smsModal.sent", { fracoes: data?.fracoesDebitadas ?? "" }));
      setCorpo("");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const fracoes = calcularFracoesSms(corpo);
  const semSaldo = saldo !== null && saldo <= 0;

  return (
    <Dialog open={Boolean(open)} onClose={handleClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={m.form}>
        <div className={m.header}>
          <div>
            <div className={m.title}>{i18n.t("smsModal.title")}</div>
            <div className={m.subtitle}>{i18n.t("smsModal.subtitle")}</div>
          </div>
          <IconButton className={m.closeButton} onClick={handleClose} disabled={sending} aria-label={i18n.t("smsModal.cancel")}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={`${m.body} ${classes.body}`}>
          {contact && (
            <div className={classes.contactCard}>
              <div className={m.avatar} style={{ width: 40, height: 40, fontSize: 13, backgroundColor: avatarColorFor(contact.id) }}>
                {contact.urlPicture && !/nopicture\.png$/i.test(contact.urlPicture) ? (
                  <img src={contact.urlPicture} alt={contact.name} />
                ) : (
                  initialsOf(contact.name)
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={classes.contactName}>{contact.name}</div>
                <div className={classes.contactNumber}>{contact.isGroup ? contact.number : formatPhone(contact.number)}</div>
              </div>
              {saldo !== null && (
                <span className={`${classes.saldoPill} ${semSaldo ? classes.saldoZero : ""}`}>
                  <SmsIcon /> {i18n.t("smsModal.balance", { saldo })}
                </span>
              )}
            </div>
          )}

          <div>
            <div className={classes.labelRow}>
              <label className={m.fieldLabel} htmlFor="sms-corpo" style={{ marginBottom: 0 }}>
                {i18n.t("smsModal.message")}
              </label>
              <span className={`${classes.counter} ${fracoes > 1 ? classes.counterWarn : ""}`}>
                {i18n.t("smsModal.counter", { chars: corpo.length, sms: fracoes })}
              </span>
            </div>
            <TextField
              id="sms-corpo"
              variant="outlined"
              className={m.input}
              placeholder={i18n.t("smsModal.placeholder")}
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              multiline
              rows={5}
              disabled={sending}
              autoFocus
            />
            <div className={m.hint}>{i18n.t("smsModal.hint")}</div>
          </div>
        </div>

        <div className={m.footer}>
          {/* Saldo projetado, como no mockup: o débito real é calculado no
              servidor no envio, então isto é estimativa pela mesma regra de
              frações. */}
          {saldo !== null && fracoes > 0 && (
            <span className={classes.saldoDepois}>
              {i18n.t("smsModal.balanceAfter", {
                saldo: Math.max(0, saldo - fracoes),
              })}
            </span>
          )}
          <div className={m.footerActions}>
            <Button variant="outlined" className={m.cancelButton} onClick={handleClose} disabled={sending}>
              {i18n.t("smsModal.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={m.submitButton}
              startIcon={<SendIcon />}
              onClick={handleEnviar}
              disabled={sending || !corpo.trim() || semSaldo}
            >
              {i18n.t("smsModal.send")}
              {sending && <CircularProgress size={22} className={m.buttonProgress} />}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default EnviarSmsModal;
